import React, { useEffect, useState, useCallback } from 'react';
import { fetchSampleVideoUrl, fetchSamplePhraseVideoUrl } from '../../../utils/TrainingHelpers';

const SamplePhrasePopup = ({
    visible,
    phrase,
    phraseId,
    videos = [],
    variations = [],
    recordedVariations = [],
    signs = [],
    onClose,
    onStart,
    initialVariation = '',
    initialStep = 'select',
    onVariationChange,
    onStepChange,
}) => {
    const [videoIndex, setVideoIndex] = useState(0);
    const [selectedVariation, setSelectedVariation] = useState(initialVariation);
    const [step, setStep] = useState(initialStep);
    const [signIndex, setSignIndex] = useState(0);
    const [signVideos, setSignVideos] = useState([]);
    const [showVariationError, setShowVariationError] = useState(false);

    useEffect(() => {
        if (visible) {
            setVideoIndex(0);
            setSelectedVariation(initialVariation);
            setStep(initialStep);
            setSignIndex(0);
            setSignVideos([]);
            setShowVariationError(false);
        }
    }, [visible, phrase, initialVariation, initialStep]);

    const filteredVariations = variations.filter(v => !recordedVariations.includes(v));

    const handlePrevVideo = useCallback(() => {
        if (videos.length > 0) {
            setVideoIndex(i => (i - 1 + videos.length) % videos.length);
        }
    }, [videos.length]);

    const handleNextVideo = useCallback(() => {
        if (videos.length > 0) {
            setVideoIndex(i => (i + 1) % videos.length);
        }
    }, [videos.length]);

    const totalSigns = signs.length + 1;

    const handlePrevSign = useCallback(() => {
        if (totalSigns > 0) {
            setSignIndex(i => (i - 1 + totalSigns) % totalSigns);
        }
    }, [totalSigns]);

    const handleNextSign = useCallback(() => {
        if (totalSigns > 0) {
            setSignIndex(i => (i + 1) % totalSigns);
        }
    }, [totalSigns]);

    useEffect(() => {
        if (visible && step === 'samples' && signs.length > 0) {
            let cancelled = false;
            const load = async () => {
                try {
                    const promises = [
                        fetchSamplePhraseVideoUrl(phraseId).catch(() => null),
                        ...signs.map((s) => fetchSampleVideoUrl(s).catch(() => null))
                    ];
                    const urls = await Promise.all(promises);
                    if (!cancelled) {
                        setSignVideos(urls);
                    }
                } catch (err) {
                    console.error('Error fetching sample videos:', err);
                }
            };
            load();
            return () => {
                cancelled = true;
            };
        }
    }, [step, signs, phraseId, visible]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose && onClose();
            } else if (step === 'select') {
                if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    if (selectedVariation) {
                        setStep('samples');
                        onStepChange && onStepChange('samples');
                    } else {
                        setShowVariationError(true);
                    }
                } else if (e.key === 'ArrowRight') {
                    handleNextVideo();
                } else if (e.key === 'ArrowLeft') {
                    handlePrevVideo();
                }
            } else if (step === 'samples') {
                if ((e.key === ' ' || e.key === 'Enter') && selectedVariation) {
                    e.preventDefault();
                    e.stopPropagation();
                    onStart && onStart(selectedVariation);
                } else if (e.key === 'ArrowRight') {
                    handleNextSign();
                } else if (e.key === 'ArrowLeft') {
                    handlePrevSign();
                } else if (e.key === 'Backspace') {
                    e.preventDefault();
                    e.stopPropagation();
                    setStep('select');
                    setSignIndex(0);
                    onStepChange && onStepChange('select');
                }
            }
        };

        if (visible) {
            window.addEventListener('keydown', handleKeyDown, true);
        }
        return () => {
            window.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [visible, selectedVariation, onClose, onStart, handleNextVideo, handlePrevVideo, handleNextSign, handlePrevSign, step, onStepChange]);

    if (!visible) return null;

    const currentVideo = videos[videoIndex];

    return (
        <div className="sample-popup-overlay">
            <div className="sample-popup">
                {step === 'select' ? (
                    <>
                        <h3 className="sample-popup-title">{phrase}</h3>
                        {currentVideo && (
                            <>
                                <video
                                    key={currentVideo}
                                    src={currentVideo}
                                    loop
                                    autoPlay
                                    muted
                                    className="sample-popup-video"
                                />
                                {videos.length > 1 && (
                                    <div className="sample-popup-buttons">
                                        <button
                                            className="sample-popup-nav-button"
                                            onClick={handlePrevVideo}
                                            aria-label="Previous video"
                                        >
                                            <img
                                                src="/icons/left-arrow.svg"
                                                className="nav-icon"
                                                alt=""
                                            />
                                        </button>
                                        <button
                                            className="sample-popup-nav-button"
                                            onClick={handleNextVideo}
                                            aria-label="Next video"
                                        >
                                            <img
                                                src="/icons/right-arrow.svg"
                                                className="nav-icon"
                                                alt=""
                                            />
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                        {filteredVariations.length > 0 && (
                            <div className={`sample-popup-instructions ${showVariationError ? 'variation-error' : ''}`}>
                                <strong>Choose the variation you want to record:</strong>
                                <ul className="variation-options">
                                    {filteredVariations.map((v) => {
                                        const label = ['normal', 'quick', 'slow'].includes(v)
                                            ? `${v} - neutral prosody`
                                            : v;
                                        return (
                                            <li key={v}>
                                                <label className="variation-option">
                                                    <input
                                                        type="radio"
                                                        name="variation"
                                                        value={v}
                                                        checked={selectedVariation === v}
                                                        onChange={() => {
                                                            setSelectedVariation(v);
                                                            setShowVariationError(false);
                                                            onVariationChange && onVariationChange(v);
                                                        }}
                                                    />
                                                    {label}
                                                </label>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}
                        <div className="sample-popup-buttons">
                            <button className="back-button" onClick={onClose}>Close (Esc)</button>
                            <button
                                className="record-button"
                                onClick={() => {
                                    if (selectedVariation) {
                                        setStep('samples');
                                        onStepChange && onStepChange('samples');
                                    } else {
                                        setShowVariationError(true);
                                    }
                                }}
                            >
                                Next (press space)
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <h3 className="sample-popup-title"><strong>PHRASE:</strong> {phrase}</h3>
                        <div className="sample-popup-variation"><strong>VARIATION:</strong> {selectedVariation}</div>
                        {signs.length > 0 && (
                            <div className="sample-popup-signs"><strong>SIGNS:</strong> {signs.join(', ')}</div>
                        )}
                        {signs.length > 0 && (
                            <h4 className="sample-popup-sign-title">
                                {signIndex === 0
                                    ? 'Complete Phrase'
                                    : `${signs[signIndex - 1]} (${signIndex}/${signs.length})`}
                            </h4>
                        )}
                        {signVideos[signIndex] ? (
                            <video
                                key={signVideos[signIndex]}
                                src={signVideos[signIndex]}
                                loop
                                autoPlay
                                muted
                                className="sample-popup-video"
                            />
                        ) : (
                            <div className="loading-spinner">
                                <div className="spinner"></div>
                            </div>
                        )}
                        {totalSigns > 1 && (
                            <div className="sample-popup-buttons">
                                <button
                                    className="sample-popup-nav-button"
                                    onClick={handlePrevSign}
                                    aria-label="Previous sign"
                                >
                                    <img
                                        src="/icons/left-arrow.svg"
                                        className="nav-icon"
                                        alt=""
                                    />
                                </button>
                                <button
                                    className="sample-popup-nav-button"
                                    onClick={handleNextSign}
                                    aria-label="Next sign"
                                >
                                    <img
                                        src="/icons/right-arrow.svg"
                                        className="nav-icon"
                                        alt=""
                                    />
                                </button>
                            </div>
                        )}
                        <div className="sample-popup-buttons">
                            <button
                                className="back-button"
                                onClick={() => {
                                    setStep('select');
                                    setSignIndex(0);
                                    onStepChange && onStepChange('select');
                                }}
                            >
                                Back (press backspace)
                            </button>
                            <button className="back-button" onClick={onClose}>Close (Esc)</button>
                            <button
                                className="record-button"
                                onClick={() => onStart && onStart(selectedVariation)}
                                disabled={!selectedVariation}
                            >
                                Start recording (press space)
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default SamplePhrasePopup;

